import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR from "swr";
import {
  type GetPropertySummaryDetailAPIRequest,
  type GetPropertySummaryDetailAPIResponse,
} from "@/features/propertySummary/types";
import { SendToTrackingService } from "@/api/handlers/errors";

interface IResponse {
  data: GetPropertySummaryDetailAPIResponse | undefined;
  isLoading: boolean;
  error: AxiosError | undefined;
}

/**
 * 物件概要の内容を取得するAPI
 *
 * 物件概要のIDを指定することでその内容を取得することができます
 *
 * @param {AxiosInstance} apiClient - APIリクエストを実行するためのAxiosインスタンス
 * @param {GetPropertySummaryDetailAPIRequest} requests - プロパティIDなどの情報を含むリクエストパラメータ
 * @returns {IResponse} フェッチされたデータ、ローディング状態、および発生したエラーを含むオブジェクト
 */
const GetPropertySummaryDetailAPI = (
  apiClient: AxiosInstance,
  requests: GetPropertySummaryDetailAPIRequest
): IResponse => {
  const fetcher = async (
    url: string
  ): Promise<GetPropertySummaryDetailAPIResponse> => {
    const { data } = await apiClient.get<GetPropertySummaryDetailAPIResponse>(
      url
    );
    return data;
  };

  const { data, isLoading, error } = useSWR<
    GetPropertySummaryDetailAPIResponse,
    AxiosError
  >(
    () => {
      if (requests.id === null) return null;
      return `/property-summary/${String(requests.id)}`;
    },
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
    return { data: undefined, isLoading, error };
  }

  return { data, isLoading, error };
};

export default GetPropertySummaryDetailAPI;
